import {
    takeEvery,
    all,
    select,
    put,
    call,
    fork
} from 'redux-saga/effects';
import queryString from 'query-string';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import urlJoin from 'url-join';
import * as staticDataMappingsApi from '../../api/staticDataMappingsApi';
import * as selectors from './selectors';
import * as actions from './actions';
import * as routerSelectors from '../../common/router/selectors';
import { encodeSortingIntoUrl, urlToSort } from '../../common/urlUtils';
import { standardProductClassDropdown } from './constants';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';

const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/staticdata/staticdatamappings');

export function* loadCommodityRows(api) {
    try {
        const commodityRows = yield select(selectors.getCommodityRows);
        if (commodityRows.length === 0) {
            yield put(actions.setCommodityLoading(true));
            const commodityData = yield call(api.commodityMapping);
            yield put(actions.setCommodityMappingRows(commodityData));
        }
    } catch (error) {
        yield put(actions.fetchStaticDataFailed(error.message));
    } finally {
        yield put(actions.setCommodityLoading(false));
    }
}

export function* loadIsdaCurve(api) {
    try {
        const isdaCurveRows = yield select(selectors.getIsdaRows);
        if (isdaCurveRows.length === 0) {
            yield put(actions.setIsdaLoading(true));
            const isdaCurveData = yield call(api.isdaCurveMapping);
            yield put(actions.setIsdaCurveRows(isdaCurveData));
        }
    } catch (error) {
        yield put(actions.fetchStaticDataFailed(error.message));
    } finally {
        yield put(actions.setIsdaLoading(false));
    }
}

export function* loadIceIsIn(api) {
    try {
        const iceIsInRows = yield select(selectors.getEquityCreditRows);
        if (iceIsInRows.length === 0) {
            yield put(actions.setEquityCreditLoading(true));
            const iceIsinData = yield call(api.iceIsinMapping);
            yield put(actions.setIceIsinRows(iceIsinData));
        }
    } catch (error) {
        yield put(actions.fetchStaticDataFailed(error.message));
    } finally {
        yield put(actions.setEquityCreditLoading(false));
    }
}

export function* loadEquitySedol(api) {
    try {
        const equitySedolRows = yield select(selectors.getEquityMappingRows);
        if (equitySedolRows.length === 0) {
            yield put(actions.setEquityMappingLoading(true));
            const equitySedolData = yield call(api.equitySedol);
            yield put(actions.setEquitySedolRows(equitySedolData));
        }
    } catch (error) {
        yield put(actions.fetchStaticDataFailed(error.message));
    } finally {
        yield put(actions.setEquityMappingLoading(false));
    }
}

export function* loadStandardProductClass(api) {
    try {
        const productClassRows = yield select(selectors.getStandardProductClassRows);
        if (productClassRows.length === 0) {
            yield put(actions.setProductClassLoading(true));
            const productClassData = yield call(api.standardProductMapping);
            yield put(actions.setProductClassRows(productClassData));
        }
    } catch (error) {
        yield put(actions.fetchStaticDataFailed(error.message));
    } finally {
        yield put(actions.setProductClassLoading(false));
    }
}

export function* loadCurrencyMapping(api) {
    try {
        const currencyMappingRows = yield select(selectors.getCurrencyMappingRows);
        if (currencyMappingRows.length === 0) {
            yield put(actions.setCurrencyMappingLoading(true));
            const currencyMappingData = yield call(api.currencyMapping);
            yield put(actions.setCurrencyMappingRows(currencyMappingData));
        }
    } catch (error) {
        yield put(actions.fetchStaticDataFailed(error.message));
    } finally {
        yield put(actions.setCurrencyMappingLoading(false));
    }
}

export function* staticDataMappingsLoaded(api) {
    yield fork(loadCommodityRows, api);
    yield fork(loadIsdaCurve, api);
    yield fork(loadIceIsIn, api);
    yield fork(loadEquitySedol, api);
    yield fork(loadStandardProductClass, api);
    yield fork(loadCurrencyMapping, api);
    const search = yield select(routerSelectors.getSearch);
    if (search) {
        yield put(actions.setAllGridSorts(urlToSort(search)));
    }
}

export function* fetchIsdaMappingOptions(api) {
    try {
        const fetchedAlready = yield select(selectors.getFetchedIsdaMappingOptions);
        if (!fetchedAlready) {
            const options = yield call(api.fetchIsdaMappingOptions);
            yield put(actions.setIsdaMappingDropdownOptions(options));
            yield put(actions.setFetchedIsdaMappingOptions(true));
        }
    } catch (error) {
        yield put(actions.fetchDropdownOptionsFailed('Failed to fetch options for Isda Mappings'));
    }
}

export function* gridSortOccured() {
    const gridFiltering = yield select(selectors.getDefaultSort);
    const gridSearch = encodeSortingIntoUrl(gridFiltering);
    const sp = queryString.parse(gridSearch);
    const newSearch = `?${queryString.stringify(sp)}`;
    const currentSearch = yield select(routerSelectors.getSearch);
    if (!currentSearch) {
        yield put(replace(`${PAGE_ROUTE}${newSearch}`));
    } else if (currentSearch !== newSearch) {
        yield put(push(`${PAGE_ROUTE}${newSearch}`));
    }
}

export function* fetchCommodityMappingOptions(api) {
    try {
        const fetchedAlready = yield select(selectors.getFetchedCommodityMappingOptions);
        if (!fetchedAlready) {
            const options = yield call(api.fetchCommodityMappingOptions);
            yield put(actions.setCommodityMappingOptions(options));
            yield put(actions.setFetchedCommodityMappingOptions(true));
        }
    } catch (error) {
        yield put(actions.fetchDropdownOptionsFailed('Failed to fetch options for Isda Commodity'));
    }
}

export function* createNewMapping(api, action) {
    try {
        switch (action.mapping) {
            case 'isda': {
                const isdaData = yield call(api.createIsdaSubCurveMapping, {
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.createIsdaRow(isdaData.id, isdaData.mappedField, isdaData.field));
                break;
            }
            case 'commodity': {
                const commodityData = yield call(api.createCommodityMapping, {
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.createCommodityRow(
                    commodityData.id,
                    commodityData.mappedField,
                    commodityData.field
                ));
                break;
            }
            case 'equity': {
                const equityData = yield call(api.createEquityMapping, {
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.createEquityRow(equityData.id,
                    equityData.mappedField,
                    equityData.field));
                break;
            }
            case 'currency': {
                const currencyData = yield call(api.createCurrencyMapping, {
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.createCurrencyMappingRow(currencyData.id,
                    currencyData.mappedField,
                    currencyData.field));
                break;
            }
            default:
        }
    } catch (error) {
        yield put(actions.createStaticDataMappingFailed(`Failed to create ${action.mapping} mapping`));
    }
}

export function* deleteRow(api, action) {
    try {
        switch (action.mapping) {
            case 'isda':
                yield call(api.deleteRowIsdaSubCurve, action.id);
                yield put(actions.deleteIsdaRow(action.id));
                break;
            case 'commodity':
                yield call(api.deleteRowCommodity, action.id);
                yield put(actions.deleteCommodityRow(action.id));
                break;
            case 'equity':
                yield call(api.deleteRowEquity, action.id);
                yield put(actions.deleteEquityRow(action.id));
                break;
            case 'currency':
                yield call(api.deleteCurrencyRow, action.id);
                yield put(actions.deleteCurrencyRow(action.id));
                break;
            default:
        }
    } catch (error) {
        yield put(actions.deleteMappingFailed(`Failed to delete row from ${action.mapping} mapping`));
    }
}

export function* fetchDropdownInfo(api, action) {
    try {
        switch (action.mapping) {
            case 'isda': {
                const fetchedAlready = yield select(selectors.getFetchedIsdaMappingOptions);
                if (!fetchedAlready) {
                    const options = yield call(api.fetchIsdaMappingOptions);
                    yield put(actions.setIsdaMappingDropdownOptions(options));
                    yield put(actions.setFetchedIsdaMappingOptions(true));
                }
                break;
            }
            case 'commodity': {
                const fetchedAlready = yield select(selectors.getFetchedCommodityMappingOptions);
                if (!fetchedAlready) {
                    const options = yield call(api.fetchCommodityMappingOptions);
                    yield put(actions.setCommodityMappingOptions(options));
                    yield put(actions.setFetchedCommodityMappingOptions(true));
                }
                break;
            }
            case 'productClass': {
                const fetchedAlready = yield select(selectors.getFetchedProductClassOptions);
                if (!fetchedAlready) {
                    yield put(actions.setProductClassOptions(standardProductClassDropdown));
                    yield put(actions.setFetchedProductClassOptions(true));
                }
                break;
            }
            case 'currency': {
                const fetchedAlready = yield select(selectors.getFetchedCurrencyOptions);
                if (!fetchedAlready) {
                    const options = yield call(api.fetchCurrencyMappingOptions);
                    yield put(actions.setCurrencyOptions(options));
                    yield put(actions.setFetchedCurrencyOptions(true));
                }
                break;
            }
            default:
        }
    } catch (error) {
        yield put(actions.fetchDropdownOptionsFailed(`Failed to fetch options for the ${action.mapping} mapping`));
    }
}

export function* editRow(api, action) {
    try {
        switch (action.mapping) {
            case 'isda':
                yield put(actions.setIsdaLoading(true));
                yield call(api.editRowIsdaSubCurve, {
                    id: action.id,
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.editIsdaRow(action.id, action.key, action.value));
                yield put(actions.setIsdaLoading(false));
                break;
            case 'commodity':
                yield put(actions.setCommodityLoading(true));
                yield call(api.editRowCommodity, {
                    id: action.id,
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.editCommodityRow(action.id, action.key, action.value));
                yield put(actions.setCommodityLoading(false));
                break;
            case 'equity':
                yield put(actions.setEquityMappingLoading(true));
                yield call(api.editRowEquity, {
                    id: action.id,
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.editEquityRow(action.id, action.key, action.value));
                yield put(actions.setEquityMappingLoading(false));
                break;
            case 'productClass':
                yield put(actions.setProductClassLoading(true));
                yield call(api.editStandardProductClass, {
                    Id: action.id,
                    InputValueTypes: action.value
                });
                yield put(actions.editProductClassRow(action.id, action.key, action.value));
                yield put(actions.setProductClassLoading(false));
                break;
            case 'currency':
                yield put(actions.setCurrencyMappingLoading(true));
                yield call(api.editCurrencyMappingRow, {
                    id: action.id,
                    inputField: action.key,
                    inputValue: action.value
                });
                yield put(actions.editCurrencyMappingRow(action.id, action.key, action.value));
                yield put(actions.setCurrencyMappingLoading(false));
                break;
            default:
        }
    } catch (error) {
        yield put(actions.editMappingFailed(`Failed to edit row in ${action.mapping} mapping`));
        yield put(actions.setAllGridsLoading(false));
    }
}

export function* handleLocationChanged(action) {
    if (action.payload.location.search !== '') {
        yield put(actions.setAllGridSorts(urlToSort(action.payload.location.search)));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.STATIC_DATA_MAPPINGS_LOADED,
            staticDataMappingsLoaded, staticDataMappingsApi),
        takeEvery(actions.CREATE_NEW_MAPPING,
            createNewMapping, staticDataMappingsApi),
        takeEvery(actions.FETCH_DROPDOWN_OPTIONS,
            fetchDropdownInfo, staticDataMappingsApi),
        takeEvery(actions.DELETE_MAPPING,
            deleteRow, staticDataMappingsApi),
        takeEvery(actions.EDIT_MAPPING,
            editRow, staticDataMappingsApi),
        takeEvery(actions.SET_GRID_SORTS, gridSortOccured),
        takeEvery(
            action => action.type === LOCATION_CHANGE
            && action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase(),
            handleLocationChanged
        )
    ]);
}
